import { CreateSingleton, LockManager, RecordMap, Partial } from "@pdfking/tfunc"

import type { RuntimeBridge } from "#public"
import type { WasmBinary } from "#public"

import { CreateWasmLoader } from "./CreateWasmLoader"
import { CreateWasmSession } from "./CreateWasmSession"

type T = number | bigint

type Locked<T> = T extends (...args: infer A) => infer R ? (...args: A) => Promise<R> : never

export type ProvisionAPI<F> = {
  [K in keyof F]: Locked<F[K]>
} & {
  instance: RuntimeBridge<T>
}

export const CreateProvisionWasm = <F extends Record<string, any>>(
  binary: WasmBinary,
  createFunctions: (i: RuntimeBridge<T>) => F
) => {
  const loader = CreateSingleton(Partial(CreateWasmLoader, binary))

  const { schedule } = LockManager()

  const session = CreateWasmSession(async () => {
    const create = await loader()
    return create()
  })

  const createAPI = (instance: RuntimeBridge<T>): ProvisionAPI<F> => {
    const fns = createFunctions(instance)
    const api = RecordMap(
      (func: any) =>
        (...args: any[]) =>
          schedule("λ", async () => func(...args)),
      fns
    ) as unknown as Omit<ProvisionAPI<F>, "instance">

    return { instance, ...api } as ProvisionAPI<F>
  }

  return async function* ProvisionWasm<R>(
    fn: (api: ProvisionAPI<F>) => AsyncGenerator<R>
  ): AsyncGenerator<R> {
    yield* session(async function* (instance: RuntimeBridge<T>) {
      yield* fn(createAPI(instance))
    })
  }
}
